import React, { useEffect } from 'react';

const formatDocType = (type: string) => {
  if (!type) return '';
  return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

export function GPDetailModal({
  gatePass,
  onClose,
}: {
  gatePass: any;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!gatePass) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-fade-in-up">
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#e2e8f0] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-bold text-[#1e293b]">Gate Pass</h2>
              <span className="text-lg font-bold text-[#4f46e5] font-mono">{gatePass.gpNumber}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
                gatePass.verificationStatus === 'VERIFIED' ? 'bg-green-50 text-green-600 border-green-200' :
                gatePass.verificationStatus === 'REJECTED' ? 'bg-red-50 text-red-600 border-red-200' :
                'bg-amber-50 text-amber-600 border-amber-200'
              }`}>
                {gatePass.verificationStatus}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#64748b]">
              <span className="font-medium text-[#1e293b]">{gatePass.partyName}</span>
              <span>&middot;</span>
              <span>{gatePass.date?.slice(0,10)} {gatePass.time?.slice(0,5)}</span>
              {gatePass.linkedDocNumber && (
                <>
                  <span>&middot;</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-links-line text-xs" />
                    {formatDocType(gatePass.linkedDocType)}: <span className="font-mono text-indigo-600">{gatePass.linkedDocLabel || gatePass.linkedDocNumber}</span>
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Details Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-[#e2e8f0] rounded-xl p-4 bg-[#f8fafc]">
              <p className="text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-2">Details</p>
              <h3 className="text-base font-bold text-[#1e293b] mb-1">{gatePass.partyName}</h3>
              <p className="text-sm text-[#64748b]">
                <span className="font-medium">Purpose:</span> {gatePass.purpose}
              </p>
            </div>
            <div className="border border-[#e2e8f0] rounded-xl p-4 bg-[#f8fafc]">
              <p className="text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-2">Transport Details</p>
              <div className="space-y-1.5 text-sm text-[#64748b]">
                <p className="flex items-center gap-2">
                  <i className="ri-truck-line w-4 text-center" />
                  Vehicle: <span className="font-medium text-[#1e293b]">{gatePass.vehicleNumber}</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-user-line w-4 text-center" />
                  Driver: <span className="font-medium text-[#1e293b]">{gatePass.driverName || '—'}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <h3 className="text-xs font-bold text-[#64748b] uppercase tracking-wider mb-3">Items</h3>
            
            <div className="border border-[#e2e8f0] rounded-xl overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[#64748b] font-medium text-xs">
                  <tr>
                    <th className="px-4 py-3 w-12 text-center">#</th>
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3">Unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {gatePass.items && gatePass.items.length > 0 ? (
                    gatePass.items.map((item: any, i: number) => (
                      <tr key={item.id} className="hover:bg-[#f8fafc]/50">
                        <td className="px-4 py-3 text-center text-[#64748b]">{i + 1}</td>
                        <td className="px-4 py-3 font-medium text-[#1e293b]">{item.itemName}</td>
                        <td className="px-4 py-3 text-right font-mono text-[#1e293b]">{item.qty}</td>
                        <td className="px-4 py-3 text-[#64748b]">{item.unit}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-[#94a3b8]">No items specified.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="bg-[#f8fafc] border-t border-[#e2e8f0] px-4 py-3 flex items-center justify-between text-sm font-medium text-[#1e293b]">
                <span>Total — {gatePass.items?.length || 0} items</span>
                <span className="font-mono pr-[4.5rem]">
                  {gatePass.items?.reduce((sum: number, item: any) => sum + (Number(item.qty) || 0), 0) || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#e2e8f0] bg-[#f8fafc] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg font-semibold bg-white border border-[#e2e8f0] text-[#64748b] hover:bg-slate-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
